/**
 * BudgetTypeBadge Component
 * Displays budget type with color coding.
 *
 * Type IDs (from BudgetType DB table):
 *  0 = General, 1 = Department, 2 = Project, 3 = Donations
 *
 * When the row carries a lookup color/name (e.g. budgetTypeColor from
 * BudgetModelView), those take precedence over the local fallback map.
 */

import { Badge, type ThemedBadgeProps } from '@/components/ui';

interface BudgetTypeBadgeProps extends Omit<ThemedBadgeProps, 'children' | 'color'> {
  typeID: number | undefined | null;
  /** Lookup name from the API row — preferred over the fallback label. */
  name?: string | null;
  /** Lookup color from the API row — preferred over the fallback color. */
  color?: string | null;
}

const TYPE_CONFIG: Record<number, { label: string; color: string }> = {
  0: { label: 'General', color: 'gray' },
  1: { label: 'Department', color: 'blue' },
  2: { label: 'Project', color: 'violet' },
  3: { label: 'Donations', color: 'green' },
};

export function BudgetTypeBadge({
  typeID,
  name,
  color,
  variant = 'light',
  size = 'sm',
  ...props
}: BudgetTypeBadgeProps) {
  if (typeID === undefined || typeID === null) {
    return (
      <Badge variant={variant} size={size} color="gray" {...props}>
        {name || 'Unknown'}
      </Badge>
    );
  }

  const config = TYPE_CONFIG[typeID];
  const label = name || config?.label || `Type ${typeID}`;
  const badgeColor = color || config?.color || 'gray';

  return (
    <Badge variant={variant} size={size} color={badgeColor} {...props}>
      {label}
    </Badge>
  );
}
